import React, { useState, useRef, useEffect } from 'react';
import { GraduationCap } from 'lucide-react';
import '../../../styles/resume-editor.css';

interface Education {
  uid: string;
  institution: string;
  degree: string;
  field_of_study?: string;
  graduation_date?: string | null;
  description?: string;
  grade?: string;
}

interface EducationCardProps {
  education: Education;
  onEdit: (education: Education) => void;
  onDelete: (uid: string) => void;
}

export const EducationCard: React.FC<EducationCardProps> = ({ education, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="resume-editor__experience-card">
      <div className="resume-editor__experience-card-header">
        <div className="resume-editor__experience-card-info">
          <h3 className="resume-editor__experience-card-title">{education.degree}</h3>
          <div className="resume-editor__experience-card-company-row">
            <div className="resume-editor__experience-card-company">
              <div className="resume-editor__experience-card-logo">
                <GraduationCap size={20} />
              </div>
              <p className="resume-editor__experience-card-subtitle">{education.institution}</p>
            </div>
          </div>
        </div>
        <div className="resume-editor__experience-card-menu-container" ref={menuRef}>
          <button 
            className="resume-editor__experience-card-menu" 
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {education.graduation_date && (
            <p className="resume-editor__experience-card-date">{formatDate(education.graduation_date)}</p>
          )}
          {menuOpen && (
            <div className="resume-editor__experience-card-dropdown">
              <button
                className="resume-editor__experience-card-dropdown-item"
                onClick={() => {
                  onEdit(education);
                  setMenuOpen(false);
                }}
              >
                Edit education
              </button>
              <button
                className="resume-editor__experience-card-dropdown-item resume-editor__experience-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(education.uid);
                  setMenuOpen(false);
                }}
              >
                Delete education
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="resume-editor__experience-card-body">
        {education.field_of_study && (
          <p className="resume-editor__experience-card-description">
            <strong>Field of Study:</strong> {education.field_of_study}
          </p>
        )}
        {education.description && (
          <p className="resume-editor__experience-card-description">{education.description}</p>
        )}
        {education.grade && (
          <p className="resume-editor__experience-card-description">
            <strong>Grade:</strong> {education.grade}
          </p>
        )}
      </div>
    </div>
  );
};

